import {
  faBan,
  faCheckCircle,
  faCheckDouble,
  faChevronDown,
  faPen,
  faPrint,
  faSave,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import EmptyList from 'components/common/EmptyList';
import Flex from 'components/common/Flex';
import SearchInputWithSuggestions from 'components/common/SearchInputWithSuggestions';
import DialogModal from 'components/modals/DialogModal';
import { useDebounce } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Breadcrumb,
  Button,
  ButtonGroup,
  Card,
  Col,
  Dropdown,
  Form,
  Row,
  Spinner
} from 'react-bootstrap';
import CurrencyInput from 'react-currency-input-field';
import { Link, useNavigate } from 'react-router-dom';
import paths from 'routes/paths';
import InvoiceService from 'services/Invoice.service';
import ProductServiceService from 'services/ProductService.service';

const InvoiceEditView = ({ invoiceId, onBack, onRefresh }) => {
  const navigate = useNavigate();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [returnItemsToPending, setReturnItemsToPending] = useState(true);

  // Estados para adicionar/editar item
  const [productServices, setProductServices] = useState([]);
  const [selectedProductServiceId, setSelectedProductServiceId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const isSelectingProductRef = useRef(false);
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const getData = useCallback(async () => {
    if (!invoiceId) return;

    setIsLoading(true);
    try {
      const response = await InvoiceService.findById(invoiceId);
      if (response && response.id) {
        setInvoice(response);
      } else {
        showErrorNotification('Fatura não encontrada');
        onBack();
      }
    } catch (error) {
      console.error('Erro ao buscar fatura:', error);
      showErrorNotification('Erro ao carregar fatura');
      onBack();
    } finally {
      setIsLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (invoiceId) {
      getData();
    }
  }, [invoiceId, getData]);

  const formatCurrency = value => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const isDisabled = invoice?.status === 'COMPLETED';

  // Buscar produtos/serviços
  const loadProductServices = async searchValue => {
    setIsLoadingProducts(true);
    try {
      const params = {
        page: 0,
        size: 100,
        inactive: false
      };

      if (searchValue && searchValue.trim()) {
        params.searchTerm = searchValue.trim();
      }

      const queryParams = new URLSearchParams(params).toString();
      const response = await ProductServiceService.list(queryParams);
      if (response.content) {
        setProductServices(response.content);
      }
    } catch (error) {
      console.error('Erro ao buscar produtos/serviços:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const debouncedLoadProductServices = useDebounce(searchValue => {
    loadProductServices(searchValue);
  }, 300);

  useEffect(() => {
    if (isSelectingProductRef.current) {
      isSelectingProductRef.current = false;
      return;
    }

    if (searchTerm.trim()) {
      debouncedLoadProductServices(searchTerm);
      setShowSuggestions(true);
    } else {
      setProductServices([]);
      setShowSuggestions(false);
    }
  }, [searchTerm]);

  const handleProductServiceSelect = product => {
    isSelectingProductRef.current = true;
    setSelectedProductServiceId(String(product.id));
    setSearchTerm(product.name || '');
    setUnitPrice(product.unitPrice || 0);
    setShowSuggestions(false);
  };

  const handleSaveItem = async () => {
    if (!selectedProductServiceId || quantity <= 0) {
      showErrorNotification(
        'Selecione um produto/serviço e informe uma quantidade válida'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      let response;
      if (editingItemId) {
        // Editar item existente
        response = await InvoiceService.updateItem(invoiceId, editingItemId, {
          quantity: parseFloat(quantity),
          unitPrice: parseFloat(unitPrice)
        });

        if (response.id || response.status === 200) {
          showSuccessNotification('Item atualizado com sucesso');
          resetForm();
          getData();
        } else {
          showErrorNotification(
            response.userMessage || 'Erro ao atualizar item'
          );
        }
      } else {
        // Adicionar novo item (não permitido conforme requisito)
        showErrorNotification('Não é possível adicionar novos itens à fatura');
      }
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      showErrorNotification('Erro ao salvar item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedProductServiceId('');
    setSearchTerm('');
    setQuantity(1);
    setUnitPrice(0);
    setShowSuggestions(false);
    setEditingItemId(null);
  };

  const handleEditItem = item => {
    if (isDisabled) return;
    setEditingItemId(item.id);
    setSelectedProductServiceId(String(item.productService.id || ''));
    setSearchTerm(item.productService.name || '');
    setQuantity(item.quantity || 1);
    setUnitPrice(item.unitPrice || 0);
    setShowSuggestions(false);
  };

  const handleDeleteItemClick = itemId => {
    setItemToDelete(itemId);
    setShowDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setItemToDelete(null);
  };

  const handleConfirmDelete = async confirmed => {
    if (!confirmed) {
      handleCloseDeleteDialog();
      return;
    }

    if (!itemToDelete) {
      handleCloseDeleteDialog();
      return;
    }

    setIsSaving(true);
    try {
      const response = await InvoiceService.deleteItem(invoiceId, itemToDelete);
      if (
        response.status === 200 ||
        response.status === 204 ||
        !response.userMessage
      ) {
        showSuccessNotification(
          'Item removido com sucesso. O item voltou para pendente.'
        );
        getData();
      } else {
        showErrorNotification(response.userMessage || 'Erro ao remover item');
      }
    } catch (error) {
      console.error('Erro ao remover item:', error);
      showErrorNotification('Erro ao remover item');
    } finally {
      setIsSaving(false);
      handleCloseDeleteDialog();
    }
  };

  const handleExportPdf = async () => {
    try {
      const response = await InvoiceService.exportPdf(invoiceId);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fatura-${invoice?.code || invoiceId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showSuccessNotification('PDF exportado com sucesso');
      } else {
        showErrorNotification('Erro ao exportar PDF');
      }
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      showErrorNotification('Erro ao exportar PDF');
    }
  };

  const handleUpdateStatus = async (newStatus, returnItems = false) => {
    if (!invoiceId) return;
    setIsUpdatingStatus(true);
    try {
      const response = await InvoiceService.updateStatus(
        invoiceId,
        newStatus,
        newStatus === 'CANCELED' ? returnItems : undefined
      );
      if (response && (response.id || response.status === 200)) {
        const statusLabels = {
          APPROVED: 'Aprovada',
          COMPLETED: 'Concluída',
          CANCELED: 'Cancelada'
        };
        showSuccessNotification(
          `Fatura ${statusLabels[newStatus] || 'atualizada'} com sucesso!`
        );

        if (newStatus === 'CANCELED') navigate(paths.billing);

        // Recarregar apenas os dados da fatura para atualizar o status localmente
        await getData();
      } else {
        showErrorNotification(
          response.userMessage || 'Erro ao atualizar status da fatura'
        );
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      showErrorNotification('Erro ao atualizar status da fatura');
    } finally {
      setIsUpdatingStatus(false);
      setShowCancelDialog(false);
    }
  };

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const getStatusActions = () => {
    if (!invoice || !invoice.status) return null;

    const status = invoice.status;

    // Status terminais: COMPLETED e CANCELED não permitem mais alterações
    if (status === 'COMPLETED' || status === 'CANCELED') {
      return null;
    }

    // DRAFT: botão principal "Aprovar", dropdown com "Concluir" e "Cancelar"
    if (status === 'DRAFT') {
      return (
        <ButtonGroup>
          <Button
            onClick={() => handleUpdateStatus('APPROVED')}
            disabled={isUpdatingStatus}
          >
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            Aprovar
          </Button>
          <Dropdown as={ButtonGroup}>
            <Dropdown.Toggle
              split
              disabled={isUpdatingStatus}
              as={CustomToggle}
            />
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleUpdateStatus('COMPLETED')}>
                <FontAwesomeIcon icon={faCheckDouble} className="me-2" />
                Concluir
              </Dropdown.Item>
              <Dropdown.Item onClick={handleCancelClick}>
                <FontAwesomeIcon icon={faBan} className="me-2" />
                Cancelar
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </ButtonGroup>
      );
    }

    // APPROVED: botão principal "Concluir", dropdown com "Cancelar"
    if (status === 'APPROVED') {
      return (
        <ButtonGroup>
          <Button
            onClick={() => handleUpdateStatus('COMPLETED')}
            disabled={isUpdatingStatus}
          >
            <FontAwesomeIcon icon={faCheckDouble} className="me-2" />
            Concluir
          </Button>
          <Dropdown as={ButtonGroup}>
            <Dropdown.Toggle
              split
              disabled={isUpdatingStatus}
              as={CustomToggle}
            />
            <Dropdown.Menu>
              <Dropdown.Item onClick={handleCancelClick}>
                <FontAwesomeIcon icon={faBan} className="me-2" />
                Cancelar
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </ButtonGroup>
      );
    }

    // Outros status: não mostrar ações
    return null;
  };

  const calculateTotals = () => {
    if (!invoice || !invoice.billableItems) {
      return { products: 0, services: 0, discount: 0, total: 0 };
    }

    let productsTotal = 0;
    let servicesTotal = 0;

    invoice.billableItems.forEach(item => {
      const itemTotal = item.totalPrice || item.quantity * item.unitPrice;
      const itemType =
        item.productService?.type || item.type || item.productServiceType;

      if (itemType === 'PRODUCT') {
        productsTotal += itemTotal;
      } else {
        servicesTotal += itemTotal;
      }
    });

    const discount = invoice.discount || 0;
    const total = productsTotal + servicesTotal - discount;

    return {
      products: productsTotal,
      services: servicesTotal,
      discount,
      total
    };
  };

  const totals = calculateTotals();
  const totalAddForm = quantity * unitPrice;

  const columns = useMemo(
    () => [
      {
        accessorKey: 'ticket.code',
        header: 'Ticket',
        meta: {
          headerProps: { className: 'text-900' }
        },
        cell: rowData => {
          const { ticket } = rowData.row.original;
          return (
            <Link to={`/tickets/${ticket.id}`} target="_blank">
              #{ticket.code} {ticket.subject}
            </Link>
          );
        }
      },
      {
        accessorKey: 'productService.name',
        header: 'Produto / Serviço',
        meta: {
          headerProps: { className: 'text-900' }
        },
        cell: rowData => {
          const { productService } = rowData.row.original;
          return <div>{productService.name || '-'}</div>;
        }
      },
      {
        accessorKey: 'quantity',
        header: 'Quantidade',
        meta: {
          headerProps: { className: 'text-900' }
        },
        cell: rowData => {
          const { quantity } = rowData.row.original;
          return <div>{quantity}</div>;
        }
      },
      {
        accessorKey: 'unitPrice',
        header: 'Unitário (R$)',
        meta: {
          headerProps: { className: 'text-900 text-end' },
          cellProps: { className: 'text-end' }
        },
        cell: rowData => {
          const { unitPrice } = rowData.row.original;
          return <div>{formatCurrency(unitPrice)}</div>;
        }
      },
      {
        accessorKey: 'total',
        header: 'Total (R$)',
        meta: {
          headerProps: { className: 'text-900 text-end' },
          cellProps: { className: 'text-end' }
        },
        cell: rowData => {
          const { totalPrice, quantity, unitPrice } = rowData.row.original;
          return (
            <div className="fw-bold">
              {formatCurrency(totalPrice || quantity * unitPrice)}
            </div>
          );
        }
      },
      {
        accessorKey: 'actions',
        header: 'Ações',
        meta: {
          headerProps: { className: 'text-900 text-center' },
          cellProps: { className: 'text-center' }
        },
        cell: rowData => {
          const item = rowData.row.original;
          return (
            <Flex justifyContent="center" className="gap-2">
              <Button
                variant="link"
                size="sm"
                className="p-1"
                onClick={() => handleEditItem(item)}
                disabled={isDisabled}
                title="Editar"
              >
                <FontAwesomeIcon icon={faPen} />
              </Button>
              <Button
                variant="link"
                size="sm"
                className="p-1"
                onClick={() => handleDeleteItemClick(item.id)}
                disabled={isDisabled || isSaving}
                title="Remover"
              >
                <FontAwesomeIcon icon={faTrash} className="text-danger" />
              </Button>
            </Flex>
          );
        }
      }
    ],
    [isDisabled, isSaving]
  );

  const tableData = useMemo(() => {
    if (!invoice || !invoice.billableItems) return [];
    return invoice.billableItems.map(item => ({
      ...item,
      total: item.totalPrice || item.quantity * item.unitPrice
    }));
  }, [invoice]);

  const table = useAdvanceTable({
    data: tableData,
    columns,
    selection: false
  });

  if (isLoading) {
    return (
      <Card>
        <Card.Body>
          <Flex justifyContent="center" className="py-5">
            <Spinner animation="border" variant="primary" />
          </Flex>
        </Card.Body>
      </Card>
    );
  }

  if (!invoice) {
    return (
      <Card>
        <Card.Body>
          <EmptyList imgSize={400} vhSize={50} />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body>
        <Breadcrumb className="mb-3">
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: paths.billing }}>
            Início
          </Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: paths.billing }}>
            Financeiro
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Editar Fatura</Breadcrumb.Item>
        </Breadcrumb>
        <Flex justifyContent="between" alignItems="center" className="mb-4">
          <div>
            <h3 className="m-0 mb-1">Editar Fatura</h3>
            <p className="text-muted mb-0">
              {invoice.code || `#${invoice.id}`} -{' '}
              {invoice.customer.name || '-'}
            </p>
          </div>
          <Flex alignItems="center" className="gap-2">
            <Button variant="falcon-default" onClick={onBack}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleExportPdf}>
              <FontAwesomeIcon icon={faPrint} className="me-2" />
              Imprimir PDF
            </Button>
            {getStatusActions()}
          </Flex>
        </Flex>

        <div className="mb-4">
          <h6 className="mb-3 fw-bold">Editar Item</h6>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Label>Produto / Serviço</Form.Label>
              <div className="position-relative">
                <SearchInputWithSuggestions
                  searchTerm={searchTerm}
                  disabled={
                    isDisabled || isSubmitting || !selectedProductServiceId
                  }
                  placeholder="Selecione um produto ou serviço..."
                  suggestions={productServices}
                  showSuggestions={
                    showSuggestions && productServices.length > 0
                  }
                  onSearchChange={e => {
                    setSearchTerm(e.target.value);
                    setShowSuggestions(e.target.value.length > 0);
                  }}
                  onSuggestionSelect={handleProductServiceSelect}
                  renderSuggestionItem={product => (
                    <div
                      key={product.id}
                      className="px-3 py-2 border-bottom cursor-pointer hover-bg-200"
                      onClick={() => handleProductServiceSelect(product)}
                      role="button"
                    >
                      <div className="fw-bold">{product.name}</div>
                      <div className="text-muted small">
                        {formatCurrency(product.unitPrice)}
                      </div>
                    </div>
                  )}
                />
                {isLoadingProducts && (
                  <div className="position-absolute end-0 top-50 translate-middle-y me-2">
                    <Spinner size="sm" />
                  </div>
                )}
              </div>
            </Col>
            <Col md={2}>
              <Form.Label>Quantidade</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={quantity}
                onChange={e => setQuantity(parseFloat(e.target.value) || 0)}
                disabled={
                  isDisabled || isSubmitting || !selectedProductServiceId
                }
              />
            </Col>
            <Col md={2}>
              <Form.Label>Preço Unitário (R$)</Form.Label>
              <CurrencyInput
                id="unitPrice"
                name="unitPrice"
                placeholder="R$ 0,00"
                value={unitPrice}
                decimalsLimit={2}
                onValueChange={value => setUnitPrice(value)}
                prefix="R$ "
                className={`form-control ${
                  !unitPrice && editingItemId ? 'is-invalid' : ''
                }`}
                intlConfig={{ locale: 'pt-BR', currency: 'BRL' }}
                disabled={
                  isDisabled || isSubmitting || !selectedProductServiceId
                }
              />
            </Col>
            <Col md={2}>
              <Form.Label>Total (R$)</Form.Label>
              <Form.Control
                type="text"
                value={formatCurrency(totalAddForm)}
                readOnly
                disabled
              />
            </Col>
            <Col md={2} as={Flex} alignItems="center">
              {editingItemId && (
                <Button
                  variant="falcon-default"
                  onClick={resetForm}
                  disabled={isSubmitting}
                  className="me-2"
                >
                  Cancelar
                </Button>
              )}
              <Button
                variant="primary"
                onClick={handleSaveItem}
                disabled={
                  isDisabled ||
                  isSubmitting ||
                  !selectedProductServiceId ||
                  quantity <= 0 ||
                  !editingItemId
                }
                className="flex-fill d-flex align-items-center justify-content-center"
              >
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Salvar
              </Button>
            </Col>
          </Row>
        </div>

        <div className="mb-4">
          <h6 className="mb-3 fw-bold">Itens da Fatura</h6>
          {!invoice.billableItems || invoice.billableItems.length === 0 ? (
            <EmptyList imgSize={300} vhSize={50} />
          ) : (
            <AdvanceTableProvider {...table}>
              <AdvanceTable
                headerClassName="text-nowrap align-middle"
                rowClassName="align-middle white-space-nowrap"
                tableProps={{
                  striped: true,
                  className: 'fs-10 mb-0 overflow-hidden'
                }}
              />
            </AdvanceTableProvider>
          )}
        </div>

        <div className="bg-body p-3 rounded">
          <Row>
            <Col md={6}>
              <div className="mb-2">
                <strong>Produtos:</strong>{' '}
                <span className="float-end">
                  {formatCurrency(totals.products)}
                </span>
              </div>
              <div className="mb-2">
                <strong>Serviços:</strong>{' '}
                <span className="float-end">
                  {formatCurrency(totals.services)}
                </span>
              </div>
            </Col>
            <Col md={6}>
              <div className="border-top pt-2 mt-2">
                <strong className="fs-5">
                  = Total Geral:{' '}
                  <span className="float-end text-primary">
                    {formatCurrency(totals.total)}
                  </span>
                </strong>
              </div>
            </Col>
          </Row>
        </div>
      </Card.Body>
      <DialogModal
        message="Tem certeza que deseja remover este item? O item voltará para pendente."
        showModal={showDeleteDialog}
        handleClose={handleCloseDeleteDialog}
        response={handleConfirmDelete}
      />

      <DialogModal
        showModal={showCancelDialog}
        handleClose={() => setShowCancelDialog(false)}
        response={confirmed => {
          if (confirmed) {
            handleUpdateStatus('CANCELED', returnItemsToPending);
          } else {
            setShowCancelDialog(false);
          }
        }}
        message={
          <div className="text-start">
            <p className="mb-3">Tem certeza que deseja cancelar esta fatura?</p>
            <Form.Check
              type="checkbox"
              id="return-items-check"
              label="Retornar itens para pendente (podem ser faturados novamente)"
              checked={returnItemsToPending}
              onChange={e => setReturnItemsToPending(e.target.checked)}
            />
          </div>
        }
        title="Cancelar Fatura"
        confirmText="Confirmar Cancelamento"
      />
    </Card>
  );
};

const CustomToggle = forwardRef(({ onClick, disabled }, ref) => (
  <Button
    style={{ borderRadius: '0 20px 20px 0', height: '100%' }}
    ref={ref}
    disabled={disabled}
    onClick={e => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <FontAwesomeIcon icon={faChevronDown} />
  </Button>
));

export default InvoiceEditView;
